import React, {useState} from 'react';
import { useNavigate} from 'react-router-dom';
import api from '../services/api';
import './Form.css';
import { notifyApplicationDraftSaved } from '../lib/applicationDrafts';
import { requiredFieldClass } from '../lib/requiredFieldClass';

const DRAFT_KEY = 'draft_trains_change';

/** 定期代金の入力欄 + 「円」表示 */
const FareInput = ({ id, name, className, value, onChange, required }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <input
            type="text"
            id={id}
            name={name}
            className={className}
            value={value}
            onChange={onChange}
            inputMode="numeric"
            pattern="[0-9]*"
            required={required}
            style={{ flex: 1 }}
        />
        <span>円</span>
    </div>
);

const TrainsChangeForm = ({ onBack, onNext, isFirstForm, isLastForm, isMultiForm, id, isDraft }) => {

    const [trains, setTrains] = useState([]);
    const [formData, setFormData] = useState({
        transport1: '',
        road1: '',
        station1: '',
        fare1: '',
        transport2: '',
        road2: '',
        station2: '',
        fare2: '',
        transport3: '',
        road3: '',
        station3: '',
        fare3:''
    });

    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);

    React.useEffect(() => {
        try {
            const saved = localStorage.getItem(DRAFT_KEY);
            if (saved) {
                const parsed = JSON.parse(saved);
                setFormData(prev => ({ ...prev, ...parsed }));
                if (parsed.trains) setTrains(parsed.trains);
            }
        } catch (_) {}
    }, []);

    const handleSaveDraft = () => {
        localStorage.setItem(DRAFT_KEY, JSON.stringify({ ...formData, trains }));
        notifyApplicationDraftSaved();
        alert('一時保存しました。申請一覧では「一時保存」と表示されます。');
    };

    const handleDeleteDraft = async () => {
        const confirmDelete = window.confirm("この下書き（一時保存データ）を削除してもよろしいですか？\n※削除すると元に戻せません。");
        if (!confirmDelete) return;

        setLoading(true);
        try {
            if (id) {
                await api.delete(`/api/applications/drafts/${id}/`);
            }
            
            // ローカルでも削除
            localStorage.removeItem(DRAFT_KEY);
            alert('下書きを削除しました。');
            navigate('/applications');
        } catch (error) {
            console.error('下書きの削除に失敗:', error);
            alert('下書きの削除に失敗しました。もう一度お試しください。');
        } finally {
            setLoading(false);
        }
    };

    const handleBack = () => { if (onBack) onBack(); else navigate('/applications'); };

    const handleAddTrain = () => {
        setTrains([...trains, { transport: '', road: '', station: '', fare: '' }]);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        if (name === 'fare1' || name === 'fare2' || name === 'fare3') {
            const digits = value.replace(/\D/g, '').slice(0, 8);
            setFormData((prev) => ({ ...prev, [name]: digits }));
            return;
        }
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleDynamicInputChange = (index, field, value) => {
        const newTrains = [...trains];
        if (field === 'fare') {
            value = value.replace(/\D/g, '').slice(0, 8);
        }
        newTrains[index][field] = value;
        setTrains(newTrains);
    };

    const handleRemoveTrain = (index) => {
        setTrains(trains.filter((_, i) => i !== index));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const isConfirmed = window.confirm("交通機関申請を送信してもよろしいですか？");
        if (!isConfirmed) return;

        const payload = {
            transport1: formData.transport1 || '',
            road1: formData.road1,
            station1: formData.station1,
            fare1: parseInt(formData.fare1, 10) || 0,
            transport2: formData.transport2 || '',
            road2: formData.road2,
            station2: formData.station2,
            fare2: parseInt(formData.fare2, 10) || 0,
            transport3: formData.transport3 || '',
            road3: formData.road3,
            station3: formData.station3,
            fare3: parseInt(formData.fare3, 10) || 0,
            additional_trains: trains.map(train => ({
                transport: train.transport || '',
                road: train.road || '',
                station: train.station || '',
                fare: parseInt(train.fare, 10) || 0
            }))
        };
        try {
            await api.post('/api/forms/submit_trains_change/', payload);
            localStorage.removeItem(DRAFT_KEY);
            alert('交通機関が正常に提出されました。');
            navigate('/applications');
        } catch (error) {
            console.error('Submission failed:', error);
            alert('申請に失敗しました。もう一度お試しください。');
        }
    };

    return(
        <div className="container form-container">
            <h3>交通機関</h3>

            <form onSubmit={handleSubmit}>
                {/* 利用機関1 */}
                <div className="form-section">
                    <h5 className="form-section-title">利用機関1</h5>
                    <div className="mb-3">
                        <label htmlFor="transport1" className="form-label">交通機関名</label>
                        <input type="text" id="transport1" name="transport1" className="form-control" value={formData.transport1} onChange={handleInputChange} placeholder="例: JR東日本" />
                    </div>
                    <div className="mb-3">
                        <label htmlFor="road1" className="form-label">路線名</label>
                        <input type="text" id="road1" name="road1" className={requiredFieldClass(formData.road1)} value={formData.road1} onChange={handleInputChange} required />
                    </div>
                    <div className="mb-3">
                        <label htmlFor="station1" className="form-label">乗車駅名～下車駅名</label>
                        <input type="text" id="station1" name="station1" className={requiredFieldClass(formData.station1)} value={formData.station1} onChange={handleInputChange} placeholder="例: 梅田 ～ 三ノ宮" required />
                    </div>
                    <div className="mb-3">
                        <label htmlFor="fare1" className="form-label">定期代金（半角数字のみ）</label>
                        <FareInput id="fare1" name="fare1" className={requiredFieldClass(formData.fare1)} value={formData.fare1} onChange={handleInputChange} required />
                    </div>
                </div>

                {/* 利用機関2 */}
                <div className="form-section">
                    <h5 className="form-section-title">利用機関2</h5>
                    <div className="mb-3">
                        <label htmlFor="transport2" className="form-label">交通機関名</label>
                        <input type="text" id="transport2" name="transport2" className="form-control" value={formData.transport2} onChange={handleInputChange} placeholder="例: 東京メトロ" />
                    </div>
                    <div className="mb-3">
                        <label htmlFor="road2" className="form-label">路線名</label>
                        <input type="text" id="road2" name="road2" className="form-control" value={formData.road2} onChange={handleInputChange} />
                    </div>
                    <div className="mb-3">
                        <label htmlFor="station2" className="form-label">乗車駅名～下車駅名</label>
                        <input type="text" id="station2" name="station2" className="form-control" value={formData.station2} onChange={handleInputChange} />
                    </div>
                    <div className="mb-3">
                        <label htmlFor="fare2" className="form-label">定期代金（半角数字のみ）</label>
                        <FareInput id="fare2" name="fare2" className="form-control" value={formData.fare2} onChange={handleInputChange} />
                    </div>
                </div>

                {/* 利用機関3 */}
                <div className="form-section">
                    <h5 className="form-section-title">利用機関3</h5>
                    <div className="mb-3">
                        <label htmlFor="transport3" className="form-label">交通機関名</label>
                        <input type="text" id="transport3" name="transport3" className="form-control" value={formData.transport3} onChange={handleInputChange} placeholder="例: 都営地下鉄" />
                    </div>
                    <div className="mb-3">
                        <label htmlFor="road3" className="form-label">路線名</label>
                        <input type="text" id="road3" name="road3" className="form-control" value={formData.road3} onChange={handleInputChange} />
                    </div>
                    <div className="mb-3">
                        <label htmlFor="station3" className="form-label">乗車駅名～下車駅名</label>
                        <input type="text" id="station3" name="station3" className="form-control" value={formData.station3} onChange={handleInputChange} />
                    </div>
                    <div className="mb-3">
                        <label htmlFor="fare3" className="form-label">定期代金（半角数字のみ）</label>
                        <FareInput id="fare3" name="fare3" className="form-control" value={formData.fare3} onChange={handleInputChange} />
                    </div>
                </div>

                {/* 追加された利用機関（4以降） */}
                {trains.map((train, index) => (
                    <div className="form-section additional-section" key={index} style={{ borderLeft: '4px solid #17a2b8' }}>
                        <h5 className="form-section-title">利用機関{index + 4}</h5>
                        <div className="mb-3">
                            <label className="form-label">交通機関名</label>
                            <input type="text" className="form-control" value={train.transport} onChange={(e) => handleDynamicInputChange(index, 'transport', e.target.value)} />
                        </div>
                        <div className="mb-3">
                            <label className="form-label">路線名</label>
                            <input type="text" className="form-control" value={train.road} onChange={(e) => handleDynamicInputChange(index, 'road', e.target.value)} />
                        </div>
                        <div className="mb-3">
                            <label className="form-label">乗車駅名～下車駅名</label>
                            <input type="text" className="form-control" value={train.station} onChange={(e) => handleDynamicInputChange(index, 'station', e.target.value)} />
                        </div>
                        <div className="mb-3">
                            <label className="form-label">定期代金</label>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <input type="text" className="form-control" value={train.fare} onChange={(e) => handleDynamicInputChange(index, 'fare', e.target.value)} inputMode="numeric" pattern="[0-9]*" style={{ flex: 1 }} />
                                <span>円</span>
                            </div>
                        </div>
                        <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => handleRemoveTrain(index)}>削除</button>
                    </div>
                ))}

                {/* 追加ボタン（動的リストの末尾に常に表示） */}
                <div className="mb-3">
                    <button type="button" className="btn btn-outline-info" onClick={handleAddTrain}>
                        ＋ 利用機関{trains.length + 4}を追加
                    </button>
                </div>

                <div className="button-group">
                    <button type="submit" className="btn-submit">申請</button>
                    <button type="button" className="btn-draft" onClick={handleSaveDraft}>一時保存</button>
                    {((id && isDraft) || localStorage.getItem(DRAFT_KEY)) && <button type="button" className="btn-delete-draft" onClick={handleDeleteDraft} disabled={loading}>下書削除</button>}
                    <button type="button" className="btn-back" onClick={handleBack}>{onBack && !isFirstForm ? '前へ' : '戻る'}</button>
                    {isMultiForm && onNext && <button type="button" className="btn-nav" onClick={onNext}>{isLastForm ? '完了' : '次へ'}</button>}
                </div>
            </form>
        </div>
    );
};

export default TrainsChangeForm;
